
import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { parsePrompt } from "@/utils/promptParser";
import { safeRemoveItem, safeSetItem } from "@/utils/storage";

interface PromptHandlerResult {
  isLoading: boolean;
  prompt: string;
  feedback: string;
  pendingMeeting: {
    dateTime: Date | null;
    attendeeName: string | null;
    duration?: number;
  } | null;
  setPrompt: (value: string) => void;
  handleSubmit: () => Promise<void>;
  processPrompt: (promptText: string) => Promise<boolean>;
}

export const usePromptHandler = (): PromptHandlerResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [feedback, setFeedback] = useState<string>("");
  const [pendingMeeting, setPendingMeeting] = useState<{
    dateTime: Date | null;
    attendeeName: string | null;
    duration?: number;
  } | null>(null);
  const { toast } = useToast();

  const processPrompt = async (promptText: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setFeedback("");
      console.log("Processing prompt:", promptText);
      
      // Extract potential name from initial prompt
      const nameMatch = promptText.match(/(?:with|meet)\s+(\w+)/i);
      const initialName = nameMatch ? nameMatch[1] : null;
      
      const parsedMeeting = await parsePrompt(promptText);
      console.log("Parsed meeting details:", parsedMeeting);

      // If we have feedback, show it
      if (parsedMeeting.feedback) {
        setFeedback(parsedMeeting.feedback);
      }

      // If we have an initial name from the prompt but the parser didn't catch it
      if (initialName && !parsedMeeting.attendeeName) {
        console.log("Setting initial name from prompt:", initialName);
        setPendingMeeting({
          dateTime: null,
          attendeeName: initialName,
          duration: parsedMeeting.duration
        });
        setFeedback(`When would you like to meet with ${initialName}?`);
        return false;
      }

      // If we have a name but no date/time, update pending meeting
      if (parsedMeeting.attendeeName && !parsedMeeting.dateTime) {
        setPendingMeeting({
          dateTime: null,
          attendeeName: parsedMeeting.attendeeName,
          duration: parsedMeeting.duration
        });
      }

      // If we have complete information
      if (parsedMeeting.isComplete && parsedMeeting.dateTime && parsedMeeting.attendeeName) {
        setPendingMeeting({
          dateTime: parsedMeeting.dateTime,
          attendeeName: parsedMeeting.attendeeName,
          duration: parsedMeeting.duration
        });

        const time = parsedMeeting.dateTime.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        const date = parsedMeeting.dateTime.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        });
        
        setFeedback(
          `I'll schedule a meeting with ${parsedMeeting.attendeeName} on ${date} at ${time}. ` +
          'Sign in to schedule.'
        );
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error processing prompt:", error);
      // Don't reset pendingMeeting here to maintain context
      setFeedback("I'm still listening. Could you provide more details about when you'd like to meet?");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!prompt) return;
    await processPrompt(prompt);
    setPrompt("");
  };

  return {
    isLoading,
    prompt,
    feedback,
    pendingMeeting,
    setPrompt,
    handleSubmit,
    processPrompt
  };
};
